import { memo, useEffect, useRef } from "react";
import { getEvents, safeCapture } from "../analytics";
import { useTranslation } from "react-i18next";
import { CashSpecialIcon, Layout, PageTitle } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { clearPhoneSkip } from "../utils/phoneGate";
import { useRequestStore } from "../store/useRequestStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import {
  generateFundRequestLink,
  type FundRequestPayoutInput,
  type FundRequestPayoutMethod,
} from "../api/fund-request";
import { getWidgetApiError, isDeadLinkError, type WidgetApiError } from "../services/api";

function destinationMaintenanceCopy(
  api: WidgetApiError | null,
): { title: string; body: string } | null {
  if (api?.code !== "FLOW_UNAVAILABLE_MAINTENANCE") return null;
  const details = (api.details ?? {}) as { title?: string; body?: string };
  const title = details.title?.trim() ?? "";
  const body = details.body?.trim() ?? "";
  if (!title && !body) return null;
  return { title, body };
}

const MIN_MINTING_VISIBLE_MS = 2000;

const holdMinimumVisible = (visibleSince: number) => {
  const remaining = MIN_MINTING_VISIBLE_MS - (Date.now() - visibleSince);
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, remaining);
  });
};

/**
 * Prefer a saved instrument id over raw details when both somehow exist. The
 * backend expands the id server-side (card token, bank account number), so the
 * mint only carries what identifies the instrument for the chosen method.
 * Always strip client-only fields (`source`, `cvsValidationId`, `numberToken`,
 * `validatedFor`) — they are not on `GenerateFundRequestPayoutDto` and fail
 * forbidNonWhitelisted.
 */
const payoutForMint = (
  payoutMethod: FundRequestPayoutMethod,
  payout: FundRequestPayoutInput
): FundRequestPayoutInput => {
  if (payout.paymentMethodId) {
    if (payoutMethod === "BANK_ACCOUNT") {
      return {
        paymentMethodId: payout.paymentMethodId,
        ...(payout.bankId ? { bankId: payout.bankId } : {}),
      };
    }
    return {
      paymentMethodId: payout.paymentMethodId,
      brand: payout.brand,
      last4: payout.last4,
      cardExpiry: payout.cardExpiry,
      cardholderName: payout.cardholderName,
    };
  }

  const {
    source: _source,
    cvsValidationId: _cvsValidationId,
    numberToken: _numberToken,
    validatedFor: _validatedFor,
    ...wirePayout
  } = payout;
  return wirePayout;
};

/**
 * "Minting your request link" screen. Reads the composed draft and mints a real
 * fund-request link via the backend, then advances to the share screen.
 * flow-request has no interactive CIP gate before mint — identity minimum
 * (name + identity country on the User) comes from partner prefill; the
 * composed destination is the payout route only. The backend resolves the
 * requester server-side.
 */
export const MintingPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { draft, setMintResult } = useRequestStore();
  const { redirectTo, linkId: linkIdFromRoute } = useRouteWithToken();

  const colorConfig = theme.whiteView;
  const { dropzoneColors, backgroundIcon } = colorConfig;

  // Guard against React StrictMode double-invoke / re-renders minting twice.
  const mintingRef = useRef(false);
  const visibleSinceRef = useRef(Date.now());

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("committing"));

    if (mintingRef.current) return;

    const linkId = linkIdFromRoute || session.data?.linkId;
    if (!draft || !linkId) {
      // Nothing to mint (e.g. hard refresh on this route) — go back to compose.
      redirectTo(APP_ROUTES.TRANSFER_PAGE);
      return;
    }

    mintingRef.current = true;

    generateFundRequestLink(linkId, {
      sourceCountry: draft.sourceCountry,
      destinationCountry: draft.destinationCountry,
      sourceCurrency: draft.sourceCurrency,
      destinationCurrency: draft.destinationCurrency,
      amount: draft.amount,
      payoutMethod: draft.payoutMethod,
      payout: payoutForMint(draft.payoutMethod, draft.payout),
      ...(draft.capabilityKey ? { capabilityKey: draft.capabilityKey } : {}),
    })
      .then(async (result) => {
        if (result.kind === "disambiguate") {
          await holdMinimumVisible(visibleSinceRef.current);
          redirectTo(APP_ROUTES.REQUEST_SCREENING_PAGE, {
            replace: true,
            state: { requiredFields: result.requiredFields },
          });
          return;
        }
        if (result.kind === "review") {
          await holdMinimumVisible(visibleSinceRef.current);
          redirectTo(APP_ROUTES.REQUEST_REVIEW_PAGE, { replace: true });
          return;
        }
        safeCapture(getEvents().REQUEST_CREATED, {
          success: true,
          request_id: result.requestId,
          link_id: result.linkId,
          amount: draft.amount,
          payout_method: draft.payoutMethod,
          capability_key: draft.capabilityKey,
          from_country: draft.sourceCountry,
          to_country: draft.destinationCountry,
        });
        if (linkId) clearPhoneSkip(linkId);
        setMintResult(result);
        useStore.getState().setAmountState({ amount: "" });
        await holdMinimumVisible(visibleSinceRef.current);
        redirectTo(APP_ROUTES.REQUEST_READY_PAGE);
      })
      .catch((err) => {
        const apiCode = getWidgetApiError(err)?.code;
        safeCapture(getEvents().REQUEST_CREATED, {
          success: false,
          error: err instanceof Error ? err.message : "Request mint failed",
          error_code: apiCode ?? "REQUEST_CREATE_FAILED",
          amount: draft.amount,
          payout_method: draft.payoutMethod,
          capability_key: draft.capabilityKey,
        });
        if (linkId) clearPhoneSkip(linkId);

        // Link expired / revoked while composing: the dead-link handler already
        // routed to the screen that explains why the link stopped working.
        if (isDeadLinkError(err)) {
          return;
        }

        const maintenance = destinationMaintenanceCopy(getWidgetApiError(err));
        if (maintenance) {
          redirectTo(APP_ROUTES.ERROR_PAGE, {
            replace: true,
            state: {
              title: maintenance.title,
              subTitle: null,
              message: maintenance.body,
              btnText: t("common.back"),
              isOut: false,
              pageUrl: APP_ROUTES.TRANSFER_PAGE,
            },
          });
          return;
        }

        // A blocked / suspended requester cannot originate requests at all —
        // retrying will never succeed, so show a terminal screen with no CTA.
        if (apiCode === "RECIPIENT_SCREENING_UNAVAILABLE") {
          redirectTo(APP_ROUTES.ERROR_PAGE, {
            replace: true,
            state: {
              title: t("requesterScreening.unavailableTitle"),
              subTitle: null,
              message: t("requesterScreening.unavailable"),
              btnText: t("common.retry"),
              isOut: false,
              pageUrl: APP_ROUTES.MINTING_PAGE,
            },
          });
          return;
        }

        if (apiCode === "USER_BLOCKED" || apiCode === "USER_SUSPENDED") {
          redirectTo(APP_ROUTES.ERROR_PAGE, {
            replace: true,
            state: {
              title: t("error.requestsDisabledTitle"),
              subTitle: null,
              message: t("error.requestsDisabledMessage"),
              btnText: null,
              isOut: false,
            },
          });
          return;
        }

        redirectTo(APP_ROUTES.ERROR_PAGE);
      });
  }, [draft, session.data?.linkId, linkIdFromRoute, setMintResult, redirectTo, t]);

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <CashSpecialIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("minting.title")}
              highlightColor={TITLE_COLOR}
              secudnary={t("minting.subtitle")}
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
